import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/common/Button/Button';
import { CheckText } from '../../../components/common/CheckText';
import { Box, Flexed } from '../../../styles/GlobalComponents';
import { color, lightTheme } from '../../../styles/GlobalStyles';
import {
  CalculatorIcon,
  Icon,
  IconWarningMark,
  InfoSmallIcon
} from '../../../styles/Icons';
import { ConversionStepsEnum } from './types';
import { CaptionText, H1, H2, P1, P2 } from '../../../styles/FontStyles';
import { LP, formatUSD } from '../../../helpers/helperFunctions';
import { ErrorToast } from '../../../components';
import { useAppSelector } from '../../../app/hooks';
import moment from 'moment';
import { TextWithTooltip } from '../../../components/common/TextWithTooltip';
import { MY_HUBS_STAKING } from '../../../routes/routes';

interface Step1Props {
  setConversionStep: Dispatch<SetStateAction<ConversionStepsEnum>>;
  pathname: string;
}

export const Step2 = ({ setConversionStep, pathname }: Step1Props) => {
  const { t } = useTranslation('my_hubs');
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [checkbox3, setCheckbox3] = useState(false);
  const [checkbox4, setCheckbox4] = useState(false);
  const [checkbox5, setCheckbox5] = useState(false);

  const showError = () => ErrorToast('Check all checkboxes');

  const handleCalculate = () => {
    if (checkbox1 === false) return showError();
    if (checkbox2 === false) return showError();
    if (checkbox3 === false) return showError();
    if (checkbox4 === false) return showError();
    if (checkbox5 === false) return showError();
    setConversionStep(ConversionStepsEnum.FINISH);
  };

  const { conversionInfo } = useAppSelector((state) => state.portfolio);

  const handleColor = (color: string) => {
    switch (color) {
      case 'green':
        return lightTheme.colors.success500;
      case 'gray':
        return lightTheme.colors.neutral500;
      case 'black':
        return '';
      default:
        break;
    }
  };

  return (
    <>
      <Box gray>
        <Flexed column center>
          <Flexed>
            <Icon mb='12'>
              <CalculatorIcon />
            </Icon>
          </Flexed>
        </Flexed>
        <P1 bold center mb='20'>
          LOYALTY POINTS CALCULATOR
        </P1>
        <Box blue>
          {conversionInfo?.infoMessages.length > 0 && (
            <Box>
              <Flexed column gap='8px'>
                {conversionInfo?.infoMessages.map(
                  ({ label, value, showIcon }: any, i: number) => {
                    return (
                      <Flexed key={i} center gap='4px'>
                        {showIcon && (
                          <TextWithTooltip
                            margin='0'
                            tooltip={
                              pathname !== MY_HUBS_STAKING ? (
                                <>
                                  Stage Bonus is calculated based the additional
                                  % of RSPs you got in the Stage you bought your
                                  NFT in.
                                  <br />
                                  <br />
                                  Stage 1: 100% Stage Bonus
                                  <br />
                                  Stage 2: 80% Stage Bonus
                                  <br />
                                  Stage 3: 70% Stage Bonus
                                  <br />
                                  Stage 4: 50% Stage Bonus
                                  <br />
                                  Stage 5: 30% Stage Bonus
                                  <br />
                                  Stage 6: 10% Stage Bonus
                                </>
                              ) : (
                                <>
                                  The Dollar Cap Value represents the current
                                  value of your Staking HUB NFT.
                                  <br />
                                  <br />
                                  You can find it on Ultron's ULXScan under the
                                  NFT Metadata menu. Simply click on the Staking
                                  HUB NFT tab and enter your NFT ID or Order ID
                                  (OID).
                                  <br />
                                </>
                              )
                            }></TextWithTooltip>
                        )}
                        <P2 color={handleColor(label.color)}>{label.text}</P2>
                        <P2 color={handleColor(value.color)}>
                          <b> {value.text}</b>
                        </P2>
                      </Flexed>
                    );
                  }
                )}
              </Flexed>
            </Box>
          )}

          <P2 bold center mb='4'>
            TOTAL NEW LOYALTY POINTS
          </P2>
          <H2 mb='4' center color={lightTheme.colors.primary900}>
            {formatUSD(conversionInfo?.amount)} {LP}
          </H2>
          <CaptionText center color={lightTheme.colors.neutral500}>
            (Use by:{' '}
            {moment
              .utc(conversionInfo?.limitDate)
              .format('MMM DD, yyyy - hh:mm')}{' '}
            UTC)
          </CaptionText>
        </Box>
      </Box>
      <CheckText
        checked={checkbox1}
        onClick={() => setCheckbox1(!checkbox1)}
        text={
          'I understand that I will receive the above amount of Loyalty Points that I can spend on purchasing any BigWhale product within the next 12 months.'
        }
      />
      <CheckText
        checked={checkbox2}
        onClick={() => setCheckbox2(!checkbox2)}
        text={
          'I understand that the Loyalty Points do not generate any bonuses when being used.'
        }
      />
      <CheckText
        checked={checkbox3}
        onClick={() => setCheckbox3(!checkbox3)}
        text={
          'I understand I will keep my Personal Orders (PO) qualification for ranks in the MaVie compensation plan.'
        }
      />
      <CheckText
        checked={checkbox4}
        onClick={() => setCheckbox4(!checkbox4)}
        text={
          'I understand that I will keep all my ULX and USDT coins already paid out from Hub NFT up to this point. Coins accumulated from Hub NFT and not yet paid out will be exchanged for Loyalty Points.'
        }
      />
      <CheckText
        checked={checkbox5}
        onClick={() => setCheckbox5(!checkbox5)}
        text={
          'I understand that my current Hub NFT will no longer be available and no future rewards will be paid out. All the Revenue Share Points will be transferred to the Revenue Pool of the BigWhale Product.'
        }
      />

      <Button
        mt='20'
        onClick={handleCalculate}
        disabled={
          !checkbox1 || !checkbox2 || !checkbox3 || !checkbox4 || !checkbox5
        }>
        Continue to final step
      </Button>
    </>
  );
};
